// components/company/tabs/CompanyNotes.jsx
import { useState } from "react";
import { Field } from "../../ui/Field";
import { 
  addCompanyNote, 
  updateCompanyNote, 
  deleteCompanyNote 
} from "../../../api";

export const CompanyNotes = ({ 
  notes, 
  companyId, 
  notify, 
  refetch 
}) => {
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [noteDraft, setNoteDraft] = useState({ note: "", source: "" });
  const [newNote, setNewNote] = useState({ note: "", source: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validateNewNote = () => {
    const newErrors = {};
    if (!newNote.note?.trim()) newErrors.note = "Note text is required";
    return newErrors;
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    const newErrors = validateNewNote();
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length > 0) return;

    setLoading(true);
    try {
      await addCompanyNote(companyId, newNote);
      setNewNote({ note: "", source: "" });
      setErrors({});
      await refetch();
      notify("Note added successfully");
    } catch (error) {
      notify(error.message);
    } finally {
      setLoading(false);
    }
  };

  const startEditNote = (note) => {
    setEditingNoteId(note.id);
    setNoteDraft({ 
      note: note.note || "", 
      source: note.source || "" 
    });
  };

  const saveNoteEdit = async () => {
    if (!editingNoteId) return;
    
    try {
      await updateCompanyNote(companyId, editingNoteId, noteDraft);
      setEditingNoteId(null);
      setNoteDraft({ note: "", source: "" });
      await refetch();
      notify("Note updated successfully");
    } catch (error) {
      notify(error.message);
    }
  };

  const cancelNoteEdit = () => {
    setEditingNoteId(null);
    setNoteDraft({ note: "", source: "" });
  };

  const removeNote = async (noteId) => {
    if (!confirm("Delete this note?")) return;
    
    try {
      await deleteCompanyNote(companyId, noteId);
      await refetch();
      notify("Note deleted successfully");
    } catch (error) {
      notify(error.message);
    }
  };

  return (
    <>
      <div className="card">
        <h3>Notes</h3>
        <ul style={{ marginTop: 12 }}>
          {notes.map((note) => (
            <li key={note.id} className="note-row">
              {editingNoteId === note.id ? (
                <div className="note-editor">
                  <Field label="Source">
                    <input 
                      value={noteDraft.source} 
                      onChange={(e) => setNoteDraft({ ...noteDraft, source: e.target.value })} 
                      placeholder="Source" 
                    />
                  </Field>
                  <Field label="Note">
                    <textarea 
                      value={noteDraft.note} 
                      onChange={(e) => setNoteDraft({ ...noteDraft, note: e.target.value })} 
                      placeholder="Note"
                      rows={3}
                    />
                  </Field>
                  <div className="note-actions">
                    <button type="button" onClick={saveNoteEdit}>
                      Save
                    </button>
                    <button 
                      type="button" 
                      className="secondary" 
                      onClick={cancelNoteEdit}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="note-display">
                  <div className="note-header">
                    <span className="kv">
                      <b>{note.created_at?.slice(0, 10)}</b>
                    </span>
                    {note.source && (
                      <span className="note-source">
                        — <em>{note.source}</em>
                      </span>
                    )}
                  </div>
                  <div className="note-content">
                    {note.note}
                  </div>
                  <div className="note-actions">
                    <button
                      type="button"
                      className="secondary"
                      onClick={() => startEditNote(note)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="danger"
                      onClick={() => removeNote(note.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
          {notes.length === 0 && (
            <li>
              <p className="kv">No notes yet.</p>
            </li>
          )}
        </ul>
      </div>

      <form className="card form-section" onSubmit={handleAddNote}>
        <h3>Add note</h3>
        <div className="form-grid">
          <Field label="Source">
            <input 
              value={newNote.source || ""} 
              onChange={(e) => setNewNote({ ...newNote, source: e.target.value })} 
              placeholder="e.g., Phone call, Email, Meeting"
            />
          </Field>
          <Field label="Note" required error={errors.note}>
            <textarea 
              value={newNote.note} 
              onChange={(e) => setNewNote({ ...newNote, note: e.target.value })}
              required
              rows={3}
              placeholder="Enter note details..."
            />
          </Field>
        </div>
        <div className="form-actions">
          <button type="submit" disabled={loading}>
            {loading ? "Adding..." : "Add note"}
          </button>
        </div>
      </form>
    </>
  );
};